/**
 * 标签页。两种外观:
 *   underline  顶栏 / 页内主切换 (当前项绿色文字 + 2px 绿色下划线, 同旧版)
 *   segmented  页内筛选 (灰底胶囊, 当前项白底)
 *
 * <Tabs>    按钮型 (role=tablist/tab, 方向键/Home/End 切换), 受控: value + onValueChange。
 *           配合 <TabPanel idPrefix value> 时传同一个 idPrefix 即可关联 aria-controls。
 * <NavTabs> 路由链接型 (NavLink, aria-current), 用于顶栏导航。
 */
import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type TabsVariant = "underline" | "segmented";

const listClass: Record<TabsVariant, string> = {
  underline: "flex items-stretch gap-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
  segmented: "inline-flex items-center gap-1 rounded-lg bg-muted p-1",
};

export function tabClass(variant: TabsVariant, active: boolean, size: "sm" | "md" = "md") {
  if (variant === "underline") {
    return cn(
      "relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 font-medium transition-colors",
      size === "sm" ? "h-9 text-sm" : "h-11 text-sm",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
      "disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4",
      "after:absolute after:inset-x-2 after:bottom-0 after:h-[3px] after:rounded-full after:transition-colors",
      active
        ? "text-primary after:bg-brand"
        : "text-muted-foreground after:bg-transparent hover:text-foreground",
    );
  }
  return cn(
    "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 font-medium transition-colors",
    size === "sm" ? "h-7 text-xs" : "h-8 text-sm",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4",
    active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
  );
}

export interface TabItem<V extends string = string> {
  value: V;
  label: ReactNode;
  icon?: LucideIcon;
  disabled?: boolean;
  /** 标签后的计数等 */
  badge?: ReactNode;
}

export interface TabsProps<V extends string = string> {
  items: ReadonlyArray<TabItem<V>>;
  value: V;
  onValueChange: (value: V) => void;
  variant?: TabsVariant;
  size?: "sm" | "md";
  /** underline 时是否画底部分隔线 (默认 true) */
  bordered?: boolean;
  /** 关联 TabPanel 用; 不传则不输出 aria-controls */
  idPrefix?: string;
  "aria-label"?: string;
  className?: string;
}

export const tabId = (prefix: string, value: string) => `${prefix}-tab-${value}`;
export const tabPanelId = (prefix: string, value: string) => `${prefix}-panel-${value}`;

export function Tabs<V extends string = string>({
  items,
  value,
  onValueChange,
  variant = "underline",
  size = "md",
  bordered = true,
  idPrefix,
  className,
  "aria-label": ariaLabel,
}: TabsProps<V>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const enabled = items.map((it, i) => (it.disabled ? -1 : i)).filter((i) => i >= 0);
    if (enabled.length === 0) return;
    const cur = enabled.indexOf(items.findIndex((it) => it.value === value));
    let next: number | null = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = enabled[(cur + 1) % enabled.length];
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = enabled[(cur - 1 + enabled.length) % enabled.length];
    else if (e.key === "Home") next = enabled[0];
    else if (e.key === "End") next = enabled[enabled.length - 1];
    if (next === null) return;
    e.preventDefault();
    onValueChange(items[next].value);
    refs.current[next]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      onKeyDown={onKeyDown}
      className={cn(listClass[variant], variant === "underline" && bordered && "border-b", className)}
    >
      {items.map((it, i) => {
        const active = it.value === value;
        const Icon = it.icon;
        return (
          <button
            key={it.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={idPrefix ? tabId(idPrefix, it.value) : undefined}
            aria-controls={idPrefix ? tabPanelId(idPrefix, it.value) : undefined}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            disabled={it.disabled}
            onClick={() => onValueChange(it.value)}
            className={tabClass(variant, active, size)}
          >
            {Icon && <Icon aria-hidden />}
            {it.label}
            {it.badge}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  idPrefix,
  value,
  className,
  children,
}: {
  idPrefix: string;
  value: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      id={tabPanelId(idPrefix, value)}
      aria-labelledby={tabId(idPrefix, value)}
      tabIndex={0}
      className={cn("focus-visible:outline-none", className)}
    >
      {children}
    </div>
  );
}

export interface NavTabItem {
  to: string;
  label: ReactNode;
  icon?: LucideIcon;
  /** 仅精确匹配时高亮 (首页 "/" 需要) */
  end?: boolean;
}

export function NavTabs({
  items,
  variant = "underline",
  size = "md",
  className,
  itemClassName,
  "aria-label": ariaLabel,
}: {
  items: ReadonlyArray<NavTabItem>;
  variant?: TabsVariant;
  size?: "sm" | "md";
  className?: string;
  /** 每个 tab 的额外样式 (如 flex-1 等宽) */
  itemClassName?: string;
  "aria-label"?: string;
}) {
  return (
    <nav aria-label={ariaLabel} className={cn(listClass[variant], className)}>
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            className={({ isActive }) => cn(tabClass(variant, isActive, size), variant === "underline" && "h-full", itemClassName)}
          >
            {Icon && <Icon aria-hidden />}
            {it.label}
          </NavLink>
        );
      })}
    </nav>
  );
}
